import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CardDescription } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Trash2,
  Pencil,
  GraduationCap,
  Search,
  DollarSign,
  ArrowRightLeft,
  Check,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Badge } from "../components/ui/badge";

function printAllStudentCards(students) {
  const printWindow = window.open("", "_blank", "width=900,height=1200");
  if (!printWindow) return;

  printWindow.document.write("<html><head><title>Print Student Cards</title>");
  printWindow.document.write(`
    <style>
      @media print {
        body { background: #fff; margin: 0; }
        .print-class-header {
          font-size: 22px;
          font-weight: bold;
          text-align: center;
          margin-bottom: 18px;
        }
        .card-grid {
          display: flex;
          flex-wrap: wrap;
          width: 100%;
          page-break-after: always;
        }
        .student-card-print {
          width: 48%; /* 2 columns */
          min-width: 0;
          box-sizing: border-box;
          border: 1px dashed #888;
          margin: 1% 1%;
          padding: 24px 10px;
          font-family: monospace;
          background: #fff;
          text-align: center;
          display: inline-block;
          vertical-align: top;
          page-break-inside: avoid;
          height: 320px;
        }
        .card-row-break {
          display: block;
          width: 100%;
          height: 0;
          page-break-after: avoid;
        }
      }
      body { background: #f8f8f8; margin: 0; }
      .print-class-header {
        font-size: 22px;
        font-weight: bold;
        text-align: center;
        margin-bottom: 18px;
      }
      .card-grid {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
      }
      .student-card-print {
        width: 48%;
        min-width: 0;
        box-sizing: border-box;
        border: 1px dashed #888;
        margin: 1% 1%;
        padding: 24px 10px;
        font-family: monospace;
        background: #fff;
        text-align: center;
        display: inline-block;
        vertical-align: top;
        height: 320px;
      }
      .card-row-break {
        width: 100%;
        height: 0;
      }
    </style>
  `);
  printWindow.document.write("</head><body>");
  // Show class name if all students are from the same class
  if (students.length > 0) {
    const className = students[0].class_name;
    const allSameClass = students.every(s => s.class_name === className);
    if (allSameClass && className) {
      printWindow.document.write(`<div class='print-class-header'>Class: ${className}</div>`);
    }
  }
  printWindow.document.write('<div class="card-grid">');
  students.forEach((student, idx) => {
    printWindow.document.write(
      `<div class="student-card-print">` +
        `<div style="font-size:18px;font-weight:700;letter-spacing:2px;margin-bottom:16px;">Student Portal Card</div>` +
        `<div style="display:flex;justify-content:space-between;margin-bottom:24px;font-size:16px;"><span>ID:</span><span style="font-weight:600;">${student.student_id_code || ""}</span></div>` +
        `<div style="display:flex;justify-content:space-between;margin-bottom:24px;font-size:16px;"><span>Name:</span><span style="font-weight:600;">${student.first_name || ""} ${student.last_name || ""}</span></div>` +
        `<div style="border-top:1px dashed #bbb;margin:16px 0;"></div>` +
        `<div style="font-size:13px;margin-bottom:8px;">Access your student portal and results at:<br><span style="font-weight:600;color:#d72660;">student.cresabel.co.zw</span></div>` +
        `<div style="font-size:13px;margin-bottom:8px;"><b>Username:</b> <span style="font-family:monospace;">${student.student_id_code || ""}</span><br><b>Password:</b> <span style="font-family:monospace;">${student.first_name || ""}</span></div>` +
        `<div style="font-size:12px;color:#555;margin-bottom:0;">You can change your password after login.</div>` +
      `</div>`
    );

    // Insert a row break after every 2 cards (2 columns per row)
    if ((idx + 1) % 2 === 0) {
      printWindow.document.write('<div class="card-row-break"></div>');
    }
    // Insert a page break after every 6 cards (6 per A4 page)
    if ((idx + 1) % 6 === 0) {
      printWindow.document.write('</div><div class="card-grid" style="page-break-before: always;">');
    }
  });

  printWindow.document.write("</div>");
  printWindow.document.write("</body></html>");
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 400);
}

function StudentPrintCard({ student }) {
  return (
    <div
      style={{
        width: 350,
        border: "1px dashed #888",
        margin: "16px auto",
        padding: 24,
        fontFamily: "monospace",
        background: "#fff",
        pageBreakInside: "avoid",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 2, marginBottom: 16 }}>
        Student Portal Card
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24, fontSize: 16 }}>
        <span>ID:</span>
        <span style={{ fontWeight: 600 }}>{student.student_id_code}</span>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24, fontSize: 16 }}>
        <span>Name:</span>
        <span style={{ fontWeight: 600 }}>
          {student.first_name} {student.last_name}
        </span>
      </div>

      <div style={{ borderTop: "1px dashed #bbb", margin: "16px 0" }} />

      <div style={{ marginBottom: 8 }}>
        <img
          src="/student-portal-qr.svg"
          alt="Student Portal QR Code"
          width={64}
          height={64}
          style={{ display: "inline-block" }}
        />
      </div>

      <div style={{ fontSize: 13, marginBottom: 8 }}>
        Access your student portal and results at:
        <br />
        <span style={{ fontWeight: 600, color: "#d72660" }}>
          student.cresabel.co.zw
        </span>
      </div>

      <div style={{ fontSize: 13, marginBottom: 8 }}>
        <b>Username:</b>{" "}
        <span style={{ fontFamily: "monospace" }}>{student.student_id_code}</span>
        <br />
        <b>Password:</b>{" "}
        <span style={{ fontFamily: "monospace" }}>{student.first_name}</span>
      </div>

      <div style={{ fontSize: 12, color: "#555", marginBottom: 0 }}>
        You can change your password after login.
        <br />
        <span style={{ color: "#d72660", fontWeight: 600 }}>
          Please update student information in Settings.
        </span>
      </div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentsPage() {
  const { user } = useAuth();

  const emptyFormData = {
    first_name: "",
    last_name: "",
    class_id: "",
    gender: "",
    parent_name: "",
    parent_phone: "",
    parent_email: "",
    home_address: "",
    special_needs: false,
    special_needs_description: "",
    date_of_birth: "",
  };

  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkStudents, setBulkStudents] = useState([
    { id: 1, first_name: "", last_name: "", class_id: "", gender: "", parent_name: "", parent_phone: "", parent_email: "", home_address: "", date_of_birth: "" },
    { id: 2, first_name: "", last_name: "", class_id: "", gender: "", parent_name: "", parent_phone: "", parent_email: "", home_address: "", date_of_birth: "" },
    { id: 3, first_name: "", last_name: "", class_id: "", gender: "", parent_name: "", parent_phone: "", parent_email: "", home_address: "", date_of_birth: "" },
    { id: 4, first_name: "", last_name: "", class_id: "", gender: "", parent_name: "", parent_phone: "", parent_email: "", home_address: "", date_of_birth: "" },
    { id: 5, first_name: "", last_name: "", class_id: "", gender: "", parent_name: "", parent_phone: "", parent_email: "", home_address: "", date_of_birth: "" },
  ]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [selectedStudentForFee, setSelectedStudentForFee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  const [selectedFilterClass, setSelectedFilterClass] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [feeFormData, setFeeFormData] = useState({
    amount_paid: "",
  });

  useEffect(() => {
    fetchStudents();
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredStudents = students.filter((student) => {
    if (
      searchTerm &&
      !(
        student.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.student_id_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.class_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    ) {
      return false;
    }

    if (filterTab === "class" && selectedFilterClass) {
      return String(student.class_id) === String(selectedFilterClass);
    } else if (filterTab === "male") {
      return student.gender === "Male";
    } else if (filterTab === "female") {
      return student.gender === "Female";
    } else if (filterTab === "special") {
      return student.special_needs === true || student.special_needs === 1;
    } else if (filterTab === "transferred") {
      return student.status === "transferred";
    }

    return true;
  });

  const fetchStudents = async () => {
    try {
      const response = await axios.get(
        `${API}/students${user?.role === "admin" ? "?include_transferred=true" : ""}`
      );
      setStudents(response.data);
    } catch (error) {
      toast.error("Failed to fetch students");
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error("Failed to fetch classes", error);
    }
  };

  const resetForm = () => {
    setEditingStudent(null);
    setFormData(emptyFormData);
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      first_name: student.first_name || "",
      last_name: student.last_name || "",
      class_id: student.class_id ? String(student.class_id) : "",
      gender: student.gender || "",
      parent_name: student.parent_name || "",
      parent_phone: student.parent_phone || "",
      parent_email: student.parent_email || "",
      home_address: student.home_address || "",
      special_needs: student.special_needs === true || student.special_needs === 1,
      special_needs_description: student.special_needs_description || "",
      date_of_birth: student.date_of_birth ? String(student.date_of_birth).slice(0, 10) : "",
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (studentId) => {
    const confirmed = window.confirm("Are you sure you want to delete this student?");
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/students/${studentId}`);
      toast.success("Student deleted successfully");
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete student");
    }
  };

  const openFeeModal = (student) => {
    setSelectedStudentForFee(student);
    setFeeFormData({ amount_paid: "" });
    setIsFeeModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        special_needs: formData.special_needs === true,
      };

      if (editingStudent) {
        await axios.put(`${API}/students/${editingStudent.id}`, payload);
        toast.success("Student updated successfully");
      } else {
        const response = await axios.post(`${API}/students`, payload);
        toast.success(
          <div>
            <p className="font-medium">Student added successfully!</p>
            <p className="text-sm mt-1">
              Student ID: <span className="font-mono">{response.data.student_id_code}</span>
            </p>
            <p className="text-sm">
              Password: <span className="font-mono">{formData.first_name.toLowerCase()}</span>
            </p>
          </div>
        );
      }

      setIsModalOpen(false);
      resetForm();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleFeeUpdate = async (e) => {
    e.preventDefault();
    if (!selectedStudentForFee) return;

    try {
      const payload = {
        amount_paid: parseFloat(feeFormData.amount_paid) || 0,
      };

      await axios.put(`${API}/students/${selectedStudentForFee.id}/fee-status`, payload);
      toast.success("Fee status updated successfully");
      setIsFeeModalOpen(false);
      setSelectedStudentForFee(null);
      setFeeFormData({ amount_paid: "" });
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update fee");
    }
  };

  const getStudentStats = () => {
    const totalEnrolled = students.length;
    const maleCount = students.filter((s) => s.gender === "Male").length;
    const femaleCount = students.filter((s) => s.gender === "Female").length;
    const specialNeedsCount = students.filter(
      (s) => s.special_needs === true || s.special_needs === 1
    ).length;

    return { totalEnrolled, maleCount, femaleCount, specialNeedsCount };
  };

  const getFeeStatusBadge = (status) => {
    switch (status) {
      case "Paid":
        return "bg-emerald-50 text-emerald-700 hover:bg-emerald-50";
      case "Partial":
        return "bg-amber-50 text-amber-700 hover:bg-amber-50";
      default:
        return "bg-red-50 text-red-700 hover:bg-red-50";
    }
  };

  return (
    <div data-testid="students-page">
      <div className="mb-8">
        {user?.role === "admin" && (
        <Button
          variant="outline"
          className="mb-2"
          onClick={() => setShowBulkImport((v) => !v)}
        >
          {showBulkImport ? "Hide Bulk Add Students" : "Bulk Add Students"}
        </Button>
        )}

        {user?.role === "admin" && showBulkImport && (
          <Card className="border-slate-200 mb-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Bulk Add Students</CardTitle>
              <CardDescription>
                Fill in the student information. Fields marked with * are required.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">First Name *</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">Last Name *</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[100px]">Class *</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[100px]">Gender</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[130px]">Parent Name *</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[130px]">Parent Phone</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[140px]">Parent Email</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[140px]">Home Address</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">Date of Birth</TableHead>
                      <TableHead className="font-medium text-slate-700 w-[50px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {bulkStudents.map((student, idx) => (
                      <TableRow key={student.id} className="table-row-hover">
                        <TableCell>
                          <Input
                            placeholder="e.g., John"
                            value={student.first_name}
                            onChange={(e) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, first_name: e.target.value } : s))
                              )
                            }
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>

                        <TableCell>
                          <Input
                            placeholder="e.g., Doe"
                            value={student.last_name}
                            onChange={(e) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, last_name: e.target.value } : s))
                              )
                            }
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>

                        <TableCell>
                          <Select
                            value={student.class_id}
                            onValueChange={(value) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, class_id: value } : s))
                              )
                            }
                          >
                            <SelectTrigger className="text-sm border-slate-200 focus:border-pink-500">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {classes.map((cls) => (
                                <SelectItem key={cls.id} value={String(cls.id)}>
                                  {cls.display_name || `${cls.grade_level} ${cls.name}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        <TableCell>
                          <Select
                            value={student.gender}
                            onValueChange={(value) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, gender: value } : s))
                              )
                            }
                          >
                            <SelectTrigger className="text-sm border-slate-200 focus:border-pink-500">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Male">Male</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>

                        <TableCell>
                          <Input
                            placeholder="e.g., Jane Doe"
                            value={student.parent_name}
                            onChange={(e) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, parent_name: e.target.value } : s))
                              )
                            }
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>

                        <TableCell>
                          <Input
                            placeholder="+1234567890"
                            value={student.parent_phone}
                            onChange={(e) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, parent_phone: e.target.value } : s))
                              )
                            }
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>

                        <TableCell>
                          <Input
                            placeholder="email@example.com"
                            value={student.parent_email}
                            onChange={(e) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, parent_email: e.target.value } : s))
                              )
                            }
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>

                        <TableCell>
                          <Input
                            placeholder="123 Main St"
                            value={student.home_address}
                            onChange={(e) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, home_address: e.target.value } : s))
                              )
                            }
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>

                        <TableCell>
                          <Input
                            type="date"
                            value={student.date_of_birth}
                            onChange={(e) =>
                              setBulkStudents((ss) =>
                                ss.map((s, i) => (i === idx ? { ...s, date_of_birth: e.target.value } : s))
                              )
                            }
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>

                        <TableCell>
                          <button
                            onClick={() =>
                              setBulkStudents((ss) => (ss.length === 1 ? ss : ss.filter((_, i) => i !== idx)))
                            }
                            className="text-slate-400 hover:text-red-500 transition-colors p-1"
                            title="Remove row"
                          >
                            <Trash2 size={18} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() =>
                    setBulkStudents((ss) => [
                      ...ss,
                      {
                        id: Date.now(),
                        first_name: "",
                        last_name: "",
                        class_id: "",
                        gender: "",
                        parent_name: "",
                        parent_phone: "",
                        parent_email: "",
                        home_address: "",
                        date_of_birth: "",
                      },
                    ])
                  }
                  variant="outline"
                  className="flex items-center gap-2 border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <Plus size={18} /> Add More Students
                </Button>

                <Button
                  onClick={async () => {
                    const validStudents = bulkStudents.filter(
                      (s) => s.first_name && s.last_name && s.class_id && s.parent_name
                    );

                    if (validStudents.length === 0) {
                      toast.error("Please fill in at least the required fields for one student");
                      return;
                    }

                    setBulkSubmitting(true);

                    try {
                      const response = await axios.post(
                        `${API}/students/bulk-add`,
                        { students: validStudents },
                        { headers: { "Content-Type": "application/json" } }
                      );

                      setBulkResult(response.data);
                      toast.success(response.data.message);
                      setBulkStudents([
                        {
                          id: 1,
                          first_name: "",
                          last_name: "",
                          class_id: "",
                          gender: "",
                          parent_name: "",
                          parent_phone: "",
                          parent_email: "",
                          home_address: "",
                          date_of_birth: "",
                        },
                      ]);
                      fetchStudents();
                    } catch (error) {
                      toast.error(error.response?.data?.message || "Failed to add students");
                    } finally {
                      setBulkSubmitting(false);
                    }
                  }}
                  disabled={bulkSubmitting}
                  className="bg-pink-500 hover:bg-pink-600 btn-press ml-auto"
                >
                  {bulkSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Adding Students...
                    </span>
                  ) : (
                    "Add Students"
                  )}
                </Button>
              </div>

              {bulkResult && (
                <Card className="border-slate-200 mt-6">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Check className="text-emerald-500" size={20} /> Add Results
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-emerald-600 font-medium">{bulkResult.message}</p>

                      {bulkResult.created_students && bulkResult.created_students.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">
                            Created Students ({bulkResult.created_students.length}):
                          </h4>
                          <div className="max-h-60 overflow-y-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-50">
                                  <TableHead>Student ID</TableHead>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Username</TableHead>
                                  <TableHead>Password</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {bulkResult.created_students.map((student, idx) => (
                                  <TableRow key={idx} className="table-row-hover">
                                    <TableCell className="font-mono">{student.student_id_code}</TableCell>
                                    <TableCell>{student.name}</TableCell>
                                    <TableCell className="font-mono">{student.username}</TableCell>
                                    <TableCell className="font-mono">{student.password}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {bulkResult.errors && bulkResult.errors.length > 0 && (
                        <div>
                          <h4 className="font-medium text-red-600 flex items-center gap-2 mb-2">
                            <span>Errors ({bulkResult.errors.length}):</span>
                          </h4>
                          <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                            {bulkResult.errors.map((err, idx) => (
                              <p key={idx} className="text-sm text-red-600">
                                {err.error}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-3">
            <GraduationCap className="text-pink-500" size={28} />
            Students
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {user?.role === "admin" ? "Manage student records and fees" : "View students in your class"}
          </p>
        </div>

        {user?.role === "admin" && (
          <Dialog
            open={isModalOpen}
            onOpenChange={(open) => {
              setIsModalOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-pink-500 hover:bg-pink-600 text-white btn-press"
                data-testid="add-student-btn"
              >
                <Plus size={18} className="mr-2" />
                Add Student
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingStudent ? "Edit Student" : "Add New Student"}
                </DialogTitle>
                <DialogDescription>
                  {editingStudent ? "Update student information" : "Fill in the details to add a new student"}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="John"
                      required
                      data-testid="student-firstname"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Doe"
                      required
                      data-testid="student-lastname"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Class</Label>
                    <Select
                      value={formData.class_id}
                      onValueChange={(value) => setFormData({ ...formData, class_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="student-class">
                        <SelectValue placeholder="Select a class" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={String(cls.id)}>
                            {cls.display_name || `${cls.grade_level} ${cls.name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger data-testid="student-gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Parent/Guardian Name</Label>
                  <Input
                    value={formData.parent_name}
                    onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                    placeholder="Jane Doe"
                    required
                    data-testid="student-parent-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Parent Phone</Label>
                    <Input
                      type="tel"
                      value={formData.parent_phone}
                      onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })}
                      placeholder="+1234567890"
                      required
                      data-testid="student-parent-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Parent Email</Label>
                    <Input
                      type="email"
                      value={formData.parent_email}
                      onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                      placeholder="jane@email.com"
                      data-testid="student-parent-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Home Address</Label>
                  <Input
                    value={formData.home_address}
                    onChange={(e) => setFormData({ ...formData, home_address: e.target.value })}
                    placeholder="123 Main St, City"
                    required
                    data-testid="student-address"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    data-testid="student-dob"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="special-needs"
                      checked={formData.special_needs}
                      onChange={(e) => setFormData({ ...formData, special_needs: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <Label htmlFor="special-needs" className="mb-0 cursor-pointer">
                      Student has special needs
                    </Label>
                  </div>
                </div>

                {formData.special_needs && (
                  <div className="space-y-2">
                    <Label>Special Needs Description</Label>
                    <Input
                      value={formData.special_needs_description}
                      onChange={(e) =>
                        setFormData({ ...formData, special_needs_description: e.target.value })
                      }
                      placeholder="Describe the specific needs..."
                      data-testid="student-special-needs-desc"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    className="bg-pink-500 hover:bg-pink-600"
                    data-testid="save-student-btn"
                  >
                    {editingStudent ? "Update" : "Add"} Student
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Dialog
        open={isFeeModalOpen}
        onOpenChange={(open) => {
          setIsFeeModalOpen(open);
          if (!open) {
            setSelectedStudentForFee(null);
            setFeeFormData({ amount_paid: "" });
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <DollarSign size={20} className="text-pink-500" />
              Record Fee Payment
            </DialogTitle>
            <DialogDescription>
              {selectedStudentForFee && (
                <span>
                  Student:{" "}
                  <strong>
                    {selectedStudentForFee.first_name} {selectedStudentForFee.last_name}
                  </strong>
                  <span className="ml-2 font-mono text-xs">
                    ({selectedStudentForFee.student_id_code})
                  </span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleFeeUpdate} className="space-y-4 mt-4">
            {selectedStudentForFee && (
              <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                <p className="text-sm text-slate-600">
                  Total Fee:{" "}
                  <strong className="font-mono">
                    ${Number(selectedStudentForFee.total_fee || 0).toFixed(2)}
                  </strong>
                </p>
                <p className="text-sm text-slate-600">
                  Already Paid:{" "}
                  <strong className="font-mono text-emerald-600">
                    ${Number(selectedStudentForFee.amount_paid || 0).toFixed(2)}
                  </strong>
                </p>
                <p className="text-sm text-slate-600">
                  Balance:{" "}
                  <strong className="font-mono text-red-600">
                    $
                    {Math.max(
                      0,
                      Number(selectedStudentForFee.total_fee || 0) -
                        Number(selectedStudentForFee.amount_paid || 0)
                    ).toFixed(2)}
                  </strong>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Payment Amount</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={feeFormData.amount_paid}
                onChange={(e) => setFeeFormData({ ...feeFormData, amount_paid: e.target.value })}
                placeholder="Enter amount being paid"
                required
                data-testid="fee-paid"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsFeeModalOpen(false);
                  setSelectedStudentForFee(null);
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                className="bg-pink-500 hover:bg-pink-600"
                data-testid="save-fee-btn"
              >
                Record Payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by name, ID, or class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="student-search"
          />
        </div>
      </div>

      {students.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Enrollment</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {getStudentStats().totalEnrolled}
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Boys</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{getStudentStats().maleCount}</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Girls</p>
              <p className="text-2xl font-bold text-pink-600 mt-1">
                {getStudentStats().femaleCount}
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Special Needs</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">
                {getStudentStats().specialNeedsCount}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        <Button
          onClick={() => {
            setFilterTab("all");
            setSelectedFilterClass(null);
          }}
          variant={filterTab === "all" ? "default" : "outline"}
          className={filterTab === "all" ? "bg-pink-500 hover:bg-pink-600" : ""}
        >
          All Students
        </Button>

        {classes.length > 0 && (
          <div className="relative gap-2">
            <Button
              onClick={() => setFilterTab("class")}
              variant={filterTab === "class" ? "default" : "outline"}
              className={filterTab === "class" ? "bg-pink-500 hover:bg-pink-600" : ""}
            >
              {filterTab === "class" && selectedFilterClass
                ? classes.find((cls) => String(cls.id) === String(selectedFilterClass))
                    ?.display_name ||
                  (() => {
                    const cls = classes.find((cls) => String(cls.id) === String(selectedFilterClass));
                    return cls ? `${cls.grade_level} ${cls.name}` : "By Class";
                  })()
                : "By Class"}
            </Button>

            {filterTab === "class" && (
              <div className="absolute top-12 left-0 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-10 min-w-[160px]">
                {classes.map((cls) => (
                  <button
                    key={cls.id}
                    onClick={() => {
                      setSelectedFilterClass(String(cls.id));
                      setTimeout(() => setFilterTab("all"), 100); // Collapse after select
                    }}
                    className={`block w-full text-left px-3 py-2 rounded text-sm ${
                      String(selectedFilterClass) === String(cls.id)
                        ? "bg-pink-100 text-pink-600"
                        : "hover:bg-slate-100"
                    }`}
                  >
                    {cls.display_name || `${cls.grade_level} ${cls.name}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={() => {
            setFilterTab("male");
            setSelectedFilterClass(null);
          }}
          variant={filterTab === "male" ? "default" : "outline"}
          className={filterTab === "male" ? "bg-blue-500 hover:bg-blue-600" : ""}
        >
          Boys
        </Button>

        <Button
          onClick={() => {
            setFilterTab("female");
            setSelectedFilterClass(null);
          }}
          variant={filterTab === "female" ? "default" : "outline"}
          className={filterTab === "female" ? "bg-pink-400 hover:bg-pink-500" : ""}
        >
          Girls
        </Button>

        <Button
          onClick={() => {
            setFilterTab("special");
            setSelectedFilterClass(null);
          }}
          variant={filterTab === "special" ? "default" : "outline"}
          className={filterTab === "special" ? "bg-amber-500 hover:bg-amber-600" : ""}
        >
          Special Needs
        </Button>

        <Button
          onClick={() => {
            setFilterTab("transferred");
            setSelectedFilterClass(null);
          }}
          variant={filterTab === "transferred" ? "default" : "outline"}
          className={filterTab === "transferred" ? "bg-orange-500 hover:bg-orange-600" : ""}
        >
          Transferred
        </Button>
      </div>

      {filteredStudents.length > 0 && (
        <div className="mb-4 flex justify-end">
          <Button
            variant="outline"
            className="text-pink-600 border-pink-200 hover:bg-pink-50"
            onClick={() => printAllStudentCards(filteredStudents)}
          >
            Print All Cards
          </Button>
        </div>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">
            Students ({filteredStudents.length})
          </CardTitle>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <GraduationCap size={48} className="mx-auto mb-4 opacity-50" />
              <p>No students found</p>
              {user?.role === "admin" && <p className="text-sm">Click "Add Student" to get started</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-medium text-slate-500">Student ID</TableHead>
                    <TableHead className="font-medium text-slate-500">Name</TableHead>
                    <TableHead className="font-medium text-slate-500">Class</TableHead>
                    <TableHead className="font-medium text-slate-500">Gender</TableHead>
                    <TableHead className="font-medium text-slate-500">Special Needs</TableHead>
                    <TableHead className="font-medium text-slate-500">Status</TableHead>

                    {user?.role === "admin" && (
                      <>
                        <TableHead className="font-medium text-slate-500">Fee Status</TableHead>
                        <TableHead className="font-medium text-slate-500">Total Fee</TableHead>
                        <TableHead className="font-medium text-slate-500">Paid</TableHead>
                        <TableHead className="font-medium text-slate-500">Pending</TableHead>
                      </>
                    )}

                    <TableHead className="font-medium text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {filteredStudents.map((student) => (
                    <TableRow
                      key={student.id}
                      className="table-row-hover"
                      data-testid={`student-row-${student.id}`}
                    >
                      <TableCell className="font-mono text-slate-600 text-sm">
                        {student.student_id_code}
                      </TableCell>

                      <TableCell className="font-medium text-slate-900">
                        {student.first_name} {student.last_name}
                      </TableCell>

                      <TableCell>
                        <Badge variant="secondary" className="bg-slate-100 text-xs">
                          {student.class_name || "N/A"}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-sm text-slate-600">
                        {student.gender || "N/A"}
                      </TableCell>

                      <TableCell>
                        {student.special_needs ? (
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-700 border-amber-200 text-xs"
                            >
                              Yes
                            </Badge>
                            {student.special_needs_description && (
                              <span className="text-xs text-slate-500 italic">
                                {student.special_needs_description}
                              </span>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-slate-500 text-xs">
                            No
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell>
                        <Badge
                          className={
                            student.status === "transferred"
                              ? "bg-orange-50 text-orange-700"
                              : "bg-emerald-50 text-emerald-700"
                          }
                          variant="outline"
                        >
                          {student.status === "transferred" ? "Transferred" : "Active"}
                        </Badge>
                      </TableCell>

                      {user?.role === "admin" && (
                        <>
                          <TableCell>
                            <Badge className={getFeeStatusBadge(student.fee_status)}>
                              {student.fee_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600 font-mono text-sm">
                            {student.total_fee?.toFixed(2) || "0.00"}
                          </TableCell>
                          <TableCell className="text-emerald-600 font-mono text-sm">
                            {student.amount_paid?.toFixed(2) || "0.00"}
                          </TableCell>
                          <TableCell className="text-red-600 font-mono text-sm">
                            {student.amount_pending?.toFixed(2) || "0.00"}
                          </TableCell>
                        </>
                      )}

                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {user?.role === "admin" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openFeeModal(student)}
                              className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                              data-testid={`update-fee-${student.id}`}
                              title="Update Fee"
                            >
                              <DollarSign size={16} />
                            </Button>
                          )}

                          {user?.role === "admin" && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(student)}
                                className="text-slate-500 hover:text-pink-500"
                                data-testid={`edit-student-${student.id}`}
                              >
                                <Pencil size={16} />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(student.id)}
                                className="text-slate-500 hover:text-red-500"
                                data-testid={`delete-student-${student.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  const action = student.status === "transferred" ? "reinstate" : "transfer";
                                  if (!window.confirm(`Are you sure you want to ${action} this student?`)) return;

                                  try {
                                    await axios.put(`${API}/students/${student.id}/${action}`);
                                    toast.success(`Student ${action}red successfully`);
                                    fetchStudents();
                                  } catch (error) {
                                    toast.error(`Failed to ${action} student`);
                                  }
                                }}
                                className={
                                  student.status === "transferred"
                                    ? "text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                                    : "text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                                }
                                title={student.status === "transferred" ? "Reinstate Student" : "Transfer Student"}
                              >
                                <ArrowRightLeft size={16} />
                              </Button>

                              <Button
                                variant="outline"
                                size="sm"
                                className="ml-1 text-pink-600 border-pink-200 hover:bg-pink-50"
                                onClick={() => {
                                  const printWindow = window.open("", "_blank", "width=400,height=600");
                                  if (!printWindow) return;

                                  const el = document.getElementById(`student-print-card-${student.id}`);
                                  if (!el) return;

                                  printWindow.document.write("<html><head><title>Print Student Card</title>");
                                  printWindow.document.write(
                                    "<style>body{margin:0;padding:0;background:#f8f8f8;}@media print{body{background:#fff;}}</style>"
                                  );
                                  printWindow.document.write("</head><body>");
                                  printWindow.document.write(el.outerHTML);
                                  printWindow.document.write("</body></html>");
                                  printWindow.document.close();
                                  printWindow.focus();
                                  setTimeout(() => printWindow.print(), 300);
                                }}
                                title="Print Student Card"
                              >
                                Print Card
                              </Button>
                            </>
                          )}
                        </div>

                        <div style={{ display: "none" }} id={`student-print-card-${student.id}`}>
                          <StudentPrintCard student={student} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}